import { useState, useEffect } from "react";
import {
  Box, Card, CardContent, CardHeader, Typography, FormControlLabel,
  FormGroup, Switch, Divider, Chip, Button, Avatar, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip,
  Badge, alpha, Grid, RadioGroup,
  Radio, FormControl, FormLabel, Autocomplete, TextField, Stack, Alert,
  AlertTitle, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Snackbar, CircularProgress, InputAdornment,
  Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import {
  CreditCardOutlined, SmsOutlined, AutorenewOutlined, SettingsOutlined,
  CheckCircleOutline, CancelOutlined, InfoOutlined, NotificationsActiveOutlined,
  LockOpenOutlined, VerifiedUserOutlined, HourglassTopOutlined,
  Add as AddIcon, Delete as DeleteIcon,
  DragIndicator as DragIcon, LocalHospital as InsIcon,
  CreditCard as IdCardIcon, SaveOutlined as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  PaletteOutlined, LightModeOutlined, DarkModeOutlined,
  TuneOutlined as TuneIcon,
  RequestPageOutlined as RequestIcon,
  LoginOutlined as LoginStyleIcon,
  InfoOutlined as LabInfoIcon,
  PaymentOutlined as ZenithMomoIcon,
  PriceChange as PriceChangeIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ShowKeyIcon,
  VisibilityOff as HideKeyIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
  PhoneIphone as PhoneIphoneIcon,
  RocketLaunchOutlined as RocketIcon,
  PhotoLibraryOutlined as LoginImagesIcon,
  AddPhotoAlternateOutlined as AddPhotoIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon,
} from "@mui/icons-material";
import Swal from "sweetalert2";
import axios from "axios";
import {
  getAllInsurancePartners, addInsurancePartner,
  updateInsurancePartner, deleteInsurancePartner,
  getIdCardAmount, saveIdCardAmount,
  complianceSettings,
  systemTheme,
  facilitySettingsAPI,
  labPriceChangeRequestsList,
  scanPriceChangeRequestsList,
  priceChangeRequestApprove,
  priceChangeRequestReject,
  zenithMomoSettings,
  zenithMomoTestOrder,
  loginSlidesAPI,
} from "../BaseURL";
import { IS_ORIGINAL_PHOTO_SITE } from "../../utils/facilityPhotos";
import { useAppTheme } from "../context/ThemeContext";
import { DEFAULT_SLIDES as LOGIN_DEFAULT_SLIDES } from "../Login/LoginSplit";

// Brand accent for this page — used as the icon-tile / accordion stripe color.
const SETTINGS_ACCENT = "#1a6b8a";

/**
 * SettingsAccordion — uniform collapsible shell for every settings panel.
 * Removes the noisy double-card look by flattening any inner <Card> that
 * the legacy section components render.
 */
function SettingsAccordion({ icon, color = SETTINGS_ACCENT, title, subtitle, action, defaultExpanded, children }) {
  return (
    <Accordion
      defaultExpanded={!!defaultExpanded}
      disableGutters
      square={false}
      elevation={0}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        overflow: "hidden",
        "&:before": { display: "none" },
        "&.Mui-expanded": { borderColor: alpha(color, 0.5) },
        // Flatten inner cards so the accordion feels like one surface.
        "& .MuiCard-root": {
          boxShadow: "none",
          border: "none",
          bgcolor: "transparent",
          backgroundImage: "none",
        },
        "& .MuiCard-root .MuiCardHeader-root": { display: "none" },
        "& .MuiCard-root .MuiDivider-root:first-of-type": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "text.secondary" }} />}
        sx={{
          px: 2,
          minHeight: 64,
          "& .MuiAccordionSummary-content": {
            alignItems: "center",
            gap: 1.5,
            my: 1,
          },
          "&.Mui-expanded": { bgcolor: alpha(color, 0.08) },
          "&:hover": { bgcolor: alpha(color, 0.04) },
        }}
      >
        <Box sx={{
          width: 36, height: 36, borderRadius: 1.75, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: alpha(color, 0.18), color,
          border: `1px solid ${alpha(color, 0.30)}`,
        }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "text.primary", lineHeight: 1.2 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: "0.76rem", color: "text.secondary", mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {action && (
          <Box onClick={(e) => e.stopPropagation()} sx={{ mr: 1 }}>
            {action}
          </Box>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: 1.5, md: 2 }, py: 2, borderTop: "1px solid", borderColor: "divider" }}>
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

const PATIENTS = [
  { id: "P001", name: "Kwame Asante",  dob: "1978-03-12" },
  { id: "P002", name: "Abena Mensah",  dob: "1990-07-25" },
  { id: "P003", name: "Kofi Boateng",  dob: "1965-11-04" },
  { id: "P004", name: "Ama Owusu",     dob: "2001-01-17" },
  { id: "P005", name: "Yaw Darko",     dob: "1985-09-30" },
  { id: "P006", name: "Efua Tetteh",   dob: "1972-06-08" },
];

const CREDIT_REQUESTS = [
  { id: "REQ001", patientId: "P009", name: "Samuel Nkrumah",  type: "Outpatient", amount: "GHS 450.00",   requestedBy: "Billing Desk", date: "2025-02-24" },
  { id: "REQ002", patientId: "P017", name: "Grace Amoah",     type: "Inpatient",  amount: "GHS 1,200.00", requestedBy: "Ward 3 Nurse", date: "2025-02-25" },
  { id: "REQ003", patientId: "P031", name: "Emmanuel Ofori",  type: "Outpatient", amount: "GHS 85.00",    requestedBy: "Billing Desk", date: "2025-02-25" },
];

function IconBox({ icon, color }) {
  return (
    <Box sx={{ width: 38, height: 38, borderRadius: 2.5, flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      bgcolor: alpha(color, 0.12), color }}>
      {icon}
    </Box>
  );
}

// 1. Credit & Billing
function CreditBillingSettings() {
  const [creditMode, setCreditMode] = useState("none");
  const [preApproved, setPreApproved] = useState([PATIENTS[0]]);
  const [saved, setSaved] = useState(false);
  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<CreditCardOutlined fontSize="small" />} color="#1a6b8a" />}
        title="Credit & Billing Permissions"
        subheader="Control which patient bills are eligible for crediting without approval"
        action={<Chip size="small" variant="outlined"
          label={creditMode === "all" ? "All Enabled" : creditMode === "none" ? "Disabled" : "Partial"}
          color={creditMode === "all" ? "success" : creditMode === "none" ? "default" : "warning"} />}
      />
      <Divider />
      <CardContent>
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ fontWeight: 700, fontSize: "0.82rem", color: "text.primary", mb: 1 }}>
            Automatic Credit Authorization
          </FormLabel>
          <RadioGroup value={creditMode} onChange={(e) => setCreditMode(e.target.value)}>
            {[
              { value: "all",        label: "All patients bills can be credited",   desc: "Any bill in the system will process credit automatically.",   color: "success.main" },
              { value: "inpatient",  label: "Only inpatient bills can be credited",  desc: "Credit applies exclusively to admitted/ward patients.",       color: "primary.main" },
              { value: "outpatient", label: "Only outpatient bills can be credited", desc: "Credit applies exclusively to OPD/clinic visits.",           color: "warning.main" },
              { value: "none",       label: "No automatic crediting",                desc: "All credit requests require manual administrative approval.", color: "error.main"   },
            ].map((opt) => (
              <Box key={opt.value} onClick={() => setCreditMode(opt.value)} sx={{
                display: "flex", alignItems: "flex-start", p: 1.2, mb: 1, borderRadius: 2,
                border: "1.5px solid", cursor: "pointer", transition: "all 0.18s",
                borderColor: creditMode === opt.value ? opt.color : "divider",
                bgcolor: creditMode === opt.value ? alpha("#1a6b8a", 0.04) : "transparent",
              }}>
                <Radio value={opt.value} size="small" sx={{ mt: -0.3, color: opt.color }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontSize: "0.85rem" }}>{opt.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{opt.desc}</Typography>
                </Box>
              </Box>
            ))}
          </RadioGroup>
        </FormControl>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1, fontSize: "0.82rem", color: "text.primary" }}>
          Pre-Approved Patients{" "}
          <Typography component="span" variant="caption" color="text.secondary">
            — always credited regardless of setting above
          </Typography>
        </Typography>
        <Autocomplete multiple options={PATIENTS} getOptionLabel={(o) => `${o.name} (${o.id})`}
          value={preApproved} onChange={(_, v) => setPreApproved(v)}
          renderInput={(params) => <TextField {...params} size="small" placeholder="Search patient name or ID" />}
          renderTags={(val, getTagProps) =>
            val.map((opt, idx) => (
              <Chip key={opt.id} label={`${opt.name} - ${opt.id}`} size="small" color="primary" variant="outlined" {...getTagProps({ index: idx })} />
            ))}
          sx={{ mb: 2 }} />
        <Button variant="contained" size="small" disableElevation onClick={() => setSaved(true)} sx={{ borderRadius: 2 }}>
          Save Credit Settings
        </Button>
        <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)}
          message="Credit settings saved successfully" anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
      </CardContent>
    </Card>
  );
}

// 2. Credit Authorization Requests
function CreditAuthorizationRequests() {
  const [requests, setRequests] = useState(CREDIT_REQUESTS);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReq, setSelectedReq] = useState(null);
  const [action, setAction] = useState("approve");
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });
  const handleAction = (req, type) => { setSelectedReq(req); setAction(type); setDialogOpen(true); };
  const confirmAction = () => {
    setRequests((prev) => prev.filter((r) => r.id !== selectedReq.id));
    setDialogOpen(false);
    setSnack({ open: true, severity: action === "approve" ? "success" : "error",
      msg: action === "approve" ? `Credit approved for ${selectedReq.name}` : `Request declined for ${selectedReq.name}` });
  };
  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<LockOpenOutlined fontSize="small" />} color="#e07b39" />}
        title="Credit Authorization Requests"
        subheader="Pending billing-side requests requiring administrative approval"
        action={<Badge badgeContent={requests.length} color="error"><HourglassTopOutlined color="action" /></Badge>}
      />
      <Divider />
      <CardContent sx={{ p: 0 }}>
        {requests.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <VerifiedUserOutlined sx={{ fontSize: 40, color: "success.light", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">No pending authorization requests</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f7fafc" }}>
                  {["Patient", "ID", "Type", "Amount", "Requested By", "Date", "Actions"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ fontWeight: 600, fontSize: "0.82rem" }}>{r.name}</TableCell>
                    <TableCell><Chip label={r.patientId} size="small" variant="outlined" /></TableCell>
                    <TableCell><Chip label={r.type} size="small" variant="filled" sx={{ fontSize: "0.72rem" }} color={r.type === "Inpatient" ? "primary" : "warning"} /></TableCell>
                    <TableCell sx={{ fontWeight: 600, color: "error.main", fontSize: "0.82rem" }}>{r.amount}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", color: "text.secondary" }}>{r.requestedBy}</TableCell>
                    <TableCell sx={{ fontSize: "0.78rem", color: "text.secondary" }}>{r.date}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Approve Credit">
                          <IconButton size="small" color="success" onClick={() => handleAction(r, "approve")} sx={{ bgcolor: alpha("#2e7d32", 0.08) }}>
                            <CheckCircleOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Decline Request">
                          <IconButton size="small" color="error" onClick={() => handleAction(r, "decline")} sx={{ bgcolor: alpha("#c62828", 0.08) }}>
                            <CancelOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{action === "approve" ? "Approve Credit Request?" : "Decline Credit Request?"}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {action === "approve"
              ? `This will authorize the credit of ${selectedReq?.amount} for ${selectedReq?.name}. The system will process this automatically.`
              : `This will decline the credit request for ${selectedReq?.name}. The billing desk will be notified.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button onClick={() => setDialogOpen(false)} size="small">Cancel</Button>
          <Button variant="contained" size="small" disableElevation onClick={confirmAction}
            sx={{ borderRadius: 2 }} color={action === "approve" ? "success" : "error"}>
            {action === "approve" ? "Confirm Approval" : "Confirm Decline"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.severity} variant="filled" sx={{ borderRadius: 2 }}>{snack.msg}</Alert>
      </Snackbar>
    </Card>
  );
}

// 3. SMS Settings
function SmsAlertSettings() {
  const [masterSms, setMasterSms] = useState(true);
  const [options, setOptions] = useState({ welcomePackage: true, onApproval: true, onBillGenerated: false, onDischarge: true, onAppointment: false });
  const [saved, setSaved] = useState(false);
  const toggle = (key) => setOptions((o) => ({ ...o, [key]: !o[key] }));
  const smsOptions = [
    { key: "welcomePackage",  label: "Welcome package SMS",       desc: "Sent to new patients upon registration" },
    { key: "onApproval",      label: "Credit approval SMS",        desc: "Notify patient when credit is approved" },
    { key: "onBillGenerated", label: "Bill generated SMS",         desc: "Alert patient when a new bill is raised" },
    { key: "onDischarge",     label: "Discharge notification SMS", desc: "Send summary on patient discharge" },
    { key: "onAppointment",   label: "Appointment reminder SMS",   desc: "Remind patients of upcoming appointments" },
  ];
  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<SmsOutlined fontSize="small" />} color="#7b3f9e" />}
        title="SMS Alert Settings"
        subheader="Configure when the system sends automated SMS notifications"
        action={
          <FormControlLabel labelPlacement="start"
            control={<Switch checked={masterSms} onChange={(e) => setMasterSms(e.target.checked)} size="small" />}
            label={<Typography variant="caption" fontWeight={700} color={masterSms ? "success.main" : "text.disabled"}>{masterSms ? "SMS ON" : "SMS OFF"}</Typography>}
          />}
      />
      <Divider />
      <CardContent>
        {!masterSms && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            <AlertTitle sx={{ fontSize: "0.82rem", fontWeight: 700 }}>SMS globally disabled</AlertTitle>
            No SMS will be sent regardless of individual settings below.
          </Alert>
        )}
        <FormGroup>
          {smsOptions.map((opt) => (
            <Box key={opt.key} sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              py: 1.2, px: 1.5, mb: 0.8, borderRadius: 2, transition: "all 0.18s",
              bgcolor: options[opt.key] && masterSms ? alpha("#7b3f9e", 0.05) : "#f8f8f8",
              border: "1px solid", borderColor: options[opt.key] && masterSms ? alpha("#7b3f9e", 0.2) : "divider",
              opacity: masterSms ? 1 : 0.5,
            }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontSize: "0.84rem" }}>{opt.label}</Typography>
                <Typography variant="caption" color="text.secondary">{opt.desc}</Typography>
              </Box>
              <Switch checked={options[opt.key]} onChange={() => toggle(opt.key)} size="small" disabled={!masterSms} />
            </Box>
          ))}
        </FormGroup>
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" size="small" disableElevation onClick={() => setSaved(true)}
            sx={{ borderRadius: 2, bgcolor: "#7b3f9e", "&:hover": { bgcolor: "#5c2d78" } }}>
            Save SMS Settings
          </Button>
        </Box>
        <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)} message="SMS settings saved" anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
      </CardContent>
    </Card>
  );
}

// 4. Auto-Update
function AutoUpdateSettings() {
  const [opts, setOpts] = useState({ masterAutoUpdate: true, billsAutoRefresh: true, stockAutoSync: false, labResultsAutoLoad: true, reportsAutoGenerate: false });
  const [saved, setSaved] = useState(false);
  const toggle = (k) => setOpts((o) => ({ ...o, [k]: !o[k] }));
  const items = [
    { key: "billsAutoRefresh",    label: "Bills auto-refresh",        desc: "Automatically refresh billing lists in real-time" },
    { key: "stockAutoSync",       label: "Stock/pharmacy auto-sync",   desc: "Sync pharmacy and store stock levels automatically" },
    { key: "labResultsAutoLoad",  label: "Lab results auto-load",      desc: "Push lab results to patient records automatically" },
    { key: "reportsAutoGenerate", label: "Reports auto-generate",      desc: "Auto-generate daily/weekly reports on schedule" },
  ];
  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<AutorenewOutlined fontSize="small" />} color="#2e8b57" />}
        title="Auto-Update Settings"
        subheader="Control system-wide automatic data refresh and sync behaviour"
        action={
          <FormControlLabel labelPlacement="start"
            control={<Switch checked={opts.masterAutoUpdate} onChange={() => toggle("masterAutoUpdate")} size="small" />}
            label={<Typography variant="caption" fontWeight={700} color={opts.masterAutoUpdate ? "success.main" : "text.disabled"}>{opts.masterAutoUpdate ? "AUTO ON" : "AUTO OFF"}</Typography>}
          />}
      />
      <Divider />
      <CardContent>
        <FormGroup>
          {items.map((item) => (
            <Box key={item.key} sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              py: 1.2, px: 1.5, mb: 0.8, borderRadius: 2, transition: "all 0.18s",
              bgcolor: opts[item.key] && opts.masterAutoUpdate ? alpha("#2e8b57", 0.05) : "#f8f8f8",
              border: "1px solid", borderColor: opts[item.key] && opts.masterAutoUpdate ? alpha("#2e8b57", 0.2) : "divider",
              opacity: opts.masterAutoUpdate ? 1 : 0.5,
            }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontSize: "0.84rem" }}>{item.label}</Typography>
                <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
              </Box>
              <Switch checked={opts[item.key]} onChange={() => toggle(item.key)} size="small" disabled={!opts.masterAutoUpdate} />
            </Box>
          ))}
        </FormGroup>
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" size="small" disableElevation onClick={() => setSaved(true)}
            sx={{ borderRadius: 2, bgcolor: "#2e8b57", "&:hover": { bgcolor: "#1e6b40" } }}>
            Save Auto-Update Settings
          </Button>
        </Box>
        <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)} message="Auto-update settings saved" anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
      </CardContent>
    </Card>
  );
}

// 5. Notifications
function NotificationSettings() {
  const [opts, setOpts] = useState({ dashboardAlerts: true, emailSummary: false, criticalAlerts: true, auditLog: true });
  const [saved, setSaved] = useState(false);
  const toggle = (k) => setOpts((o) => ({ ...o, [k]: !o[k] }));
  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<NotificationsActiveOutlined fontSize="small" />} color="#c0392b" />}
        title="Notification & Alert Preferences"
        subheader="System notifications for administrators and authorised staff"
      />
      <Divider />
      <CardContent>
        <FormGroup>
          {[
            { key: "dashboardAlerts", label: "Dashboard alert banners", desc: "Show system alerts on the admin dashboard" },
            { key: "emailSummary",    label: "Daily email summary",      desc: "Send a daily digest email to the admin account" },
            { key: "criticalAlerts",  label: "Critical event alerts",    desc: "Immediate alerts for critical billing or system events" },
            { key: "auditLog",        label: "Audit log recording",      desc: "Log all administrative actions for compliance review" },
          ].map((item) => (
            <Box key={item.key} sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              py: 1.2, px: 1.5, mb: 0.8, borderRadius: 2, transition: "all 0.18s",
              bgcolor: opts[item.key] ? alpha("#c0392b", 0.04) : "#f8f8f8",
              border: "1px solid", borderColor: opts[item.key] ? alpha("#c0392b", 0.18) : "divider",
            }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontSize: "0.84rem" }}>{item.label}</Typography>
                <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
              </Box>
              <Switch checked={opts[item.key]} onChange={() => toggle(item.key)} size="small" />
            </Box>
          ))}
        </FormGroup>
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" size="small" disableElevation onClick={() => setSaved(true)}
            sx={{ borderRadius: 2, bgcolor: "#c0392b", "&:hover": { bgcolor: "#922b21" } }}>
            Save Notification Settings
          </Button>
        </Box>
        <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)} message="Notification settings saved" anchorOrigin={{ vertical: "bottom", horizontal: "center" }} />
      </CardContent>
    </Card>
  );
}

// 6. Insurance Partners (integrated from InsurancePartnerSettings.jsx)
function InsurancePartnerSettings() {
  const [partners, setPartners] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [newName,  setNewName]  = useState("");
  const [newShort, setNewShort] = useState("");
  const [adding,   setAdding]   = useState(false);

  const user     = JSON.parse(localStorage.getItem("user") || "{}");
  const username = user?.username || user?.name || "admin";

  const loadPartners = () => {
    setLoading(true);
    fetch(getAllInsurancePartners)
      .then((r) => r.json())
      .then((data) => { if (data?.success) setPartners(data.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadPartners(); }, []);

  const activeCount = partners.filter((p) => p.is_active).length;

  const handleAdd = () => {
    if (!newName.trim()) return;
    setAdding(true);
    fetch(addInsurancePartner, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: newName.trim(), short_name: newShort.trim() || null, is_active: 1, sort_order: partners.length, created_by: username }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.success) { setNewName(""); setNewShort(""); loadPartners(); }
        else Swal.fire("Error", data.message || "Failed to add", "error");
      })
      .catch(() => Swal.fire("Error", "Network error", "error"))
      .finally(() => setAdding(false));
  };

  const handleToggle = (partner) => {
    const newActive = partner.is_active ? 0 : 1;
    if (newActive === 1 && activeCount >= 10) {
      Swal.fire("Limit Reached", "Only 10 partners can be active on the card at once. Deactivate one first.", "warning");
      return;
    }
    fetch(updateInsurancePartner, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...partner, is_active: newActive }),
    })
      .then((r) => r.json())
      .then((data) => { if (data?.success) loadPartners(); else Swal.fire("Error", data.message, "error"); })
      .catch(() => {});
  };

  const handleDelete = (partner) => {
    Swal.fire({ title: "Delete partner?", text: `Remove "${partner.name}" from the list?`, icon: "warning",
      showCancelButton: true, confirmButtonColor: "#d33", confirmButtonText: "Yes, delete" })
      .then((result) => {
        if (!result.isConfirmed) return;
        fetch(deleteInsurancePartner, {
          method: "DELETE", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: partner.id }),
        })
          .then((r) => r.json())
          .then((data) => { if (data?.success) loadPartners(); else Swal.fire("Error", data.message, "error"); })
          .catch(() => {});
      });
  };

  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<InsIcon fontSize="small" />} color="#1a6b8a" />}
        title="Insurance Partners"
        subheader="Manage partners displayed on the patient ID card (up to 10 active)"
        action={
          <Chip size="small" variant="outlined"
            label={`${activeCount} / 10 active`}
            color={activeCount >= 10 ? "warning" : "success"} />
        }
      />
      <Divider />
      <CardContent>
        <Box sx={{ display: "flex", gap: 1, mb: 2.5, alignItems: "flex-start" }}>
          <TextField size="small" label="Partner name" value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            sx={{ flex: 2 }} />
          <TextField size="small" label="Short name" value={newShort}
            onChange={(e) => setNewShort(e.target.value)}
            sx={{ flex: 1 }} inputProps={{ maxLength: 10 }} />
          <Button variant="contained" size="small" disableElevation onClick={handleAdd}
            disabled={adding || !newName.trim()}
            startIcon={adding ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
            sx={{ height: 40, whiteSpace: "nowrap", borderRadius: 2 }}>
            Add
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : partners.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: "center", fontSize: "0.85rem" }}>
            No insurance partners yet. Add one above.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
            {partners.map((p) => (
              <Box key={p.id} sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                px: 1.5, py: 1, borderRadius: 2, transition: "all 0.2s",
                background: p.is_active ? alpha("#1a6b8a", 0.04) : "#fafafa",
                border: "1px solid", borderColor: p.is_active ? alpha("#1a6b8a", 0.15) : "divider",
                opacity: p.is_active ? 1 : 0.6,
              }}>
                <DragIcon sx={{ color: "#ccc", fontSize: 18, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography fontWeight={600} fontSize={13} noWrap>{p.name}</Typography>
                  {p.short_name && <Typography variant="caption" color="text.secondary">{p.short_name}</Typography>}
                </Box>
                <Chip size="small" variant="outlined" sx={{ fontSize: "0.7rem", mr: 0.5 }}
                  label={p.is_active ? "Active" : "Hidden"}
                  color={p.is_active ? "success" : "default"} />
                <Tooltip title={p.is_active ? "Deactivate" : "Activate"}>
                  <Switch size="small" checked={Boolean(p.is_active)} onChange={() => handleToggle(p)} />
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => handleDelete(p)} sx={{ color: "error.light" }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ))}
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
          Toggle the switch to show or hide a partner on the ID card. Only the first 10 active partners are printed.
        </Typography>
      </CardContent>
    </Card>
  );
}


// 7. ID Card Amount Setup
function IdCardAmountSettings() {
  const [amount,  setAmount]  = useState("");
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState("");

  const user     = JSON.parse(localStorage.getItem("user") || "{}");
  const username = user?.username || user?.name || "admin";

  useEffect(() => {
    fetch(getIdCardAmount)
      .then((r) => r.json())
      .then((data) => {
        if (data?.success && data?.data?.amount != null)
          setAmount(String(data.data.amount));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = () => {
    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed < 0) {
      setError("Please enter a valid amount (0 or greater).");
      return;
    }
    setError("");
    setSaving(true);
    fetch(saveIdCardAmount, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ amount: parsed, updated_by: username }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.success) setSaved(true);
        else setError(data?.message || "Failed to save.");
      })
      .catch(() => setError("Network error. Please try again."))
      .finally(() => setSaving(false));
  };

  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<IdCardIcon fontSize="small" />} color="#0277bd" />}
        title="ID Card Amount Setup"
        subheader="Set the fee charged to patients for printing an ID card"
        action={
          <Chip size="small" variant="outlined" color="info"
            label={loading ? "Loading..." : amount !== "" ? ("GHS " + parseFloat(amount || 0).toFixed(2)) : "Not set"} />
        }
      />
      <Divider />
      <CardContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              This amount is charged whenever a patient ID card is printed or re-printed.
              Set to <strong>0</strong> to make ID cards free of charge.
            </Typography>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="ID Card Fee"
                type="number"
                value={amount}
                onChange={(e) => { setAmount(e.target.value); setError(""); }}
                onKeyDown={(e) => e.key === "Enter" && handleSave()}
                size="small"
                sx={{ width: 220 }}
                inputProps={{ min: 0, step: "0.01" }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="body2" fontWeight={700} color="text.secondary">GHS</Typography>
                    </InputAdornment>
                  ),
                }}
                error={Boolean(error)}
                helperText={error || " "}
              />
              <Button
                variant="contained" size="small" disableElevation
                onClick={handleSave}
                disabled={saving || amount === ""}
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                sx={{ height: 40, borderRadius: 2, bgcolor: "#0277bd", "&:hover": { bgcolor: "#01579b" } }}
              >
                {saving ? "Saving..." : "Save Amount"}
              </Button>
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 600 }}>
                Quick presets:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {["0", "2.00", "5.00", "10.00", "20.00", "50.00"].map((preset) => (
                  <Chip
                    key={preset}
                    label={preset === "0" ? "Free" : ("GHS " + preset)}
                    size="small"
                    variant={amount === preset ? "filled" : "outlined"}
                    color={amount === preset ? "info" : "default"}
                    onClick={() => { setAmount(preset); setError(""); }}
                    sx={{ cursor: "pointer", fontWeight: 600, fontSize: "0.72rem" }}
                  />
                ))}
              </Stack>
            </Box>
          </>
        )}
      </CardContent>

      <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="success" variant="filled" sx={{ borderRadius: 2 }}>
          ID card fee updated successfully
        </Alert>
      </Snackbar>
    </Card>
  );
}

// 8. General placeholder
function GeneralSettings() {
  return (
    <Card sx={{ borderStyle: "dashed", bgcolor: "#fafcfe" }}>
      <CardHeader
        avatar={<IconBox icon={<SettingsOutlined fontSize="small" />} color="#888" />}
        title="Additional Settings"
        subheader="Reserved for future configuration modules"
      />
      <Divider />
      <CardContent>
        <Box sx={{ minHeight: 120, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "text.disabled", gap: 1 }}>
          <InfoOutlined sx={{ fontSize: 36 }} />
          <Typography variant="body2">More settings will appear here</Typography>
          <Typography variant="caption">Contact your system administrator to configure additional modules.</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

// ROOT
// Compliance — Vitals & Nursing Notes per-day rule
function ComplianceSettings() {
  const [startDate, setStartDate]   = useState("");
  const [draftDate, setDraftDate]   = useState("");
  const [minPerDay, setMinPerDay]   = useState(3);
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [pwdOpen, setPwdOpen]       = useState(false);
  const [username, setUsername]     = useState("");
  const [password, setPassword]     = useState("");
  const [error, setError]           = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(complianceSettings);
      if (data?.success) {
        setStartDate(data.startDate || "");
        setDraftDate(data.startDate || "");
        setMinPerDay(data.minPerDay || 3);
      }
    } catch (err) {
      console.error("Failed to load compliance settings:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSaveClick = () => {
    setError("");
    if (!draftDate) { setError("Please pick a start date."); return; }
    if (draftDate === startDate) { setError("No changes to save."); return; }
    setUsername("");
    setPassword("");
    setPwdOpen(true);
  };

  const submitUpdate = async () => {
    if (!username.trim() || !password) {
      setError("Username and password are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.post(complianceSettings, {
        username: username.trim(),
        password,
        startDate: draftDate,
      });
      if (data?.success) {
        setStartDate(data.startDate);
        setDraftDate(data.startDate);
        setPwdOpen(false);
        Swal.fire({
          icon: "success",
          title: "Compliance start date updated",
          text: `Effective from ${data.startDate}.`,
          timer: 2200,
          showConfirmButton: false,
        });
      } else {
        setError(data?.message || "Update failed.");
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.message || "Update failed.";
      if (status === 401) setError("Incorrect username or password.");
      else if (status === 403) setError("Only a System Administrator can update this setting.");
      else setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<HourglassTopOutlined />} color="#b45309" />}
        title="Admission Compliance"
        subheader={`Minimum ${minPerDay} vitals & ${minPerDay} nursing notes per admission day`}
      />
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Admissions whose admission date is <strong>before</strong> the start date below are exempt
          and will not block discharge. Pick a future or current date to phase the rule in cleanly.
        </Typography>

        <Stack spacing={1.25}>
          <TextField
            label="Compliance Start Date"
            type="date"
            value={draftDate}
            onChange={(e) => setDraftDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            disabled={loading}
            size="small"
            fullWidth
          />
          {startDate && (
            <Typography variant="caption" color="text.secondary">
              Currently effective from <strong>{startDate}</strong>.
            </Typography>
          )}
          {error && !pwdOpen && (
            <Alert severity="error" variant="outlined" sx={{ py: 0.25 }}>{error}</Alert>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 0.5 }}>
            <Button
              size="small"
              onClick={() => { setDraftDate(startDate); setError(""); }}
              disabled={loading || saving || draftDate === startDate}
            >
              Reset
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={onSaveClick}
              disabled={loading || saving || draftDate === startDate}
            >
              Update
            </Button>
          </Box>
        </Stack>
      </CardContent>

      <Dialog open={pwdOpen} onClose={() => !saving && setPwdOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Confirm — System Administrator</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5, fontSize: '0.85rem' }}>
            New start date: <strong>{draftDate}</strong>. Enter your admin credentials to apply.
          </DialogContentText>
          <Stack spacing={1.25}>
            <TextField
              label="Username"
              size="small"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              size="small"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
            />
            {error && (
              <Alert severity="error" variant="outlined" sx={{ py: 0.25 }}>{error}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={() => setPwdOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={submitUpdate}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : null}
          >
            {saving ? "Saving…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// System Theme — admins set the facility-wide default. Each user can still
// override it from the top-bar toggle, but the value chosen here becomes the
// theme any logged-in user with no personal preference sees, and the theme
// shown on the login screen before anyone is authenticated.
// ─────────────────────────────────────────────────────────────────────────────
const THEME_OPTIONS = [
  {
    id: "default",
    label: "Default (Light)",
    desc: "Bright surfaces with the original green palette. Best for well-lit clinical workstations.",
    color: "#2d8c73",
    icon: <LightModeOutlined fontSize="small" />,
  },
  {
    id: "black",
    label: "Black (Dark)",
    desc: "Pure-black surfaces with blurple accents. Reduces glare on overnight shifts and OLED displays.",
    color: "#5468FF",
    icon: <DarkModeOutlined fontSize="small" />,
  },
];

function SystemThemeSettings() {
  const { themeId: liveThemeId, setThemeId } = useAppTheme();
  const [savedTheme, setSavedTheme] = useState("default");
  const [draftTheme, setDraftTheme] = useState("default");
  const [loading, setLoading]       = useState(false);
  const [saving, setSaving]         = useState(false);
  const [pwdOpen, setPwdOpen]       = useState(false);
  const [username, setUsername]     = useState("");
  const [password, setPassword]     = useState("");
  const [error, setError]           = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(systemTheme);
      if (data?.success && data.themeId) {
        setSavedTheme(data.themeId);
        setDraftTheme(data.themeId);
      }
    } catch (err) {
      console.error("Failed to load system theme:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSaveClick = () => {
    setError("");
    if (draftTheme === savedTheme) { setError("No changes to save."); return; }
    setUsername("");
    setPassword("");
    setPwdOpen(true);
  };

  const submitUpdate = async () => {
    if (!username.trim() || !password) {
      setError("Username and password are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const { data } = await axios.post(systemTheme, {
        username: username.trim(),
        password,
        themeId: draftTheme,
      });
      if (data?.success) {
        setSavedTheme(data.themeId);
        setDraftTheme(data.themeId);
        setPwdOpen(false);
        Swal.fire({
          icon: "success",
          title: "Default theme updated",
          text: "New visitors and users without a personal preference will see this theme.",
          timer: 2400,
          showConfirmButton: false,
        });
      } else {
        setError(data?.message || "Update failed.");
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || err.message || "Update failed.";
      if (status === 401) setError("Incorrect username or password.");
      else if (status === 403) setError("Only a System Administrator can change the default theme.");
      else setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<PaletteOutlined fontSize="small" />} color="#5468FF" />}
        title="Default System Theme"
        subheader="Applied to the login screen and to any user who hasn't set a personal preference"
      />
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Pick the theme everyone should land on by default. Individual users can still switch their own view
          from the top-right toggle &mdash; their choice is saved per-account and overrides this default for them.
        </Typography>

        <Grid container spacing={1.5}>
          {THEME_OPTIONS.map((opt) => {
            const selected = draftTheme === opt.id;
            return (
              <Grid item xs={12} sm={6} key={opt.id}>
                <Box
                  onClick={() => !loading && setDraftTheme(opt.id)}
                  sx={{
                    cursor: loading ? "default" : "pointer",
                    p: 1.5,
                    borderRadius: 2,
                    border: "1.5px solid",
                    borderColor: selected ? opt.color : "divider",
                    bgcolor: selected ? alpha(opt.color, 0.06) : "transparent",
                    transition: "all 0.18s",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.25,
                    "&:hover": loading ? {} : { borderColor: alpha(opt.color, 0.6) },
                  }}
                >
                  <Radio
                    checked={selected}
                    value={opt.id}
                    size="small"
                    sx={{ mt: -0.5, color: opt.color, "&.Mui-checked": { color: opt.color } }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
                      <Box sx={{ color: opt.color, display: "flex" }}>{opt.icon}</Box>
                      <Typography variant="subtitle2" sx={{ fontSize: "0.88rem", fontWeight: 700 }}>
                        {opt.label}
                      </Typography>
                      {savedTheme === opt.id && (
                        <Chip label="Current Default" size="small" sx={{
                          ml: 0.5, height: 18, fontSize: "0.65rem", fontWeight: 700,
                          bgcolor: alpha(opt.color, 0.15), color: opt.color,
                        }} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      {opt.desc}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.75 }}>
          <InfoOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="caption" color="text.secondary">
            Your live view is using <strong>{THEME_OPTIONS.find(t => t.id === liveThemeId)?.label || liveThemeId}</strong>.
            Use <em>Preview</em> to try the draft on your account without changing the system default.
          </Typography>
        </Stack>

        {error && !pwdOpen && (
          <Alert severity="error" variant="outlined" sx={{ py: 0.25, mt: 1.5 }}>{error}</Alert>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1.5 }}>
          <Button
            size="small"
            onClick={() => setThemeId(draftTheme)}
            disabled={loading || draftTheme === liveThemeId}
          >
            Preview on my account
          </Button>
          <Button
            size="small"
            onClick={() => { setDraftTheme(savedTheme); setError(""); }}
            disabled={loading || saving || draftTheme === savedTheme}
          >
            Reset
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={onSaveClick}
            disabled={loading || saving || draftTheme === savedTheme}
          >
            Set as Default
          </Button>
        </Box>
      </CardContent>

      <Dialog open={pwdOpen} onClose={() => !saving && setPwdOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Confirm — System Administrator</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5, fontSize: '0.85rem' }}>
            New default theme: <strong>{THEME_OPTIONS.find(t => t.id === draftTheme)?.label || draftTheme}</strong>.
            Enter your admin credentials to apply.
          </DialogContentText>
          <Stack spacing={1.25}>
            <TextField
              label="Username"
              size="small"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoFocus
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              size="small"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
            />
            {error && (
              <Alert severity="error" variant="outlined" sx={{ py: 0.25 }}>{error}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={() => setPwdOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={submitUpdate}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : null}
          >
            {saving ? "Saving…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

// ── Payment Gateway (Zenith GlobalPay MoMo) ─────────────────────────────
// Credentials live in `facility_settings` under gp_* keys (migration 042),
// so a site can be switched from the test merchant to its live one without
// touching .env or restarting the server.
//
// Two round-trip rules make this safe to hand to a non-technical admin:
//   • The GET never returns the stored API key — only a masked marker. The
//     PUT only overwrites the key when a fresh value is typed, so editing
//     an unrelated field can't blank out the credential.
//   • "Test credentials" creates a real 0.10 GHS order through the same
//     SecurePaymentRequest the live flow uses. It proves the key and GPID
//     authenticate, and issues no debit prompt to anyone.
function PaymentGatewaySettings() {
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [testing, setTesting] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [keyDirty, setKeyDirty] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState({
    enabled: true, mock: true,
    sms_on_prompt: true, sms_on_receipt: true,
    api_key: '', api_key_masked: '',
    gpid: '', base_url: '', merchant_name: '',
  });

  const updatedBy = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("userInfo") || "{}");
      return u?.name || u?.username || "admin";
    } catch (_) { return "admin"; }
  })();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(zenithMomoSettings);
      if (data?.success) {
        const next = { ...data.data, api_key: "" };
        setForm(next);
        setInitial(next);
        setKeyDirty(false);
        setTestResult(null);
      }
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Could not load payment settings",
        text: err?.response?.data?.message || err.message,
      });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const set = (k) => (e) => {
    const v = e.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    if (k === "api_key") setKeyDirty(true);
  };

  const dirty = !!initial && (
    form.enabled        !== initial.enabled        ||
    form.mock           !== initial.mock           ||
    form.sms_on_prompt  !== initial.sms_on_prompt  ||
    form.sms_on_receipt !== initial.sms_on_receipt ||
    form.gpid           !== initial.gpid           ||
    form.base_url       !== initial.base_url       ||
    form.merchant_name  !== initial.merchant_name  ||
    (keyDirty && form.api_key.trim() !== "")
  );

  const handleSave = async () => {
    // Live mode with no key anywhere is the one combination that silently
    // breaks every payment, so it's blocked at the form rather than
    // discovered by a cashier at the counter.
    if (form.enabled && !form.mock && !form.api_key_masked && !form.api_key.trim()) {
      await Swal.fire({
        icon: "warning",
        title: "API key required",
        text: "Live mode needs the API key Zenith issued. Paste it in, or leave test mode on.",
      });
      return;
    }
    if (form.enabled && !String(form.gpid).trim()) {
      await Swal.fire({ icon: "warning", title: "GPID required", text: "Enter the merchant GPID Zenith assigned to this facility." });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        enabled:        !!form.enabled,
        mock:           !!form.mock,
        sms_on_prompt:  !!form.sms_on_prompt,
        sms_on_receipt: !!form.sms_on_receipt,
        gpid:           form.gpid,
        base_url:       form.base_url,
        merchant_name:  form.merchant_name,
        updatedBy,
      };
      if (keyDirty && form.api_key.trim()) payload.api_key = form.api_key.trim();
      const { data } = await axios.put(zenithMomoSettings, payload);
      if (!data?.success) throw new Error(data?.message || "Save failed");
      await Swal.fire({
        icon: "success",
        title: "Payment gateway saved",
        text: "New credentials take effect immediately — no restart needed.",
        timer: 2200, showConfirmButton: false,
      });
      await load();
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Save failed",
        text: err?.response?.data?.message || err.message || "Unable to save.",
      });
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await axios.post(zenithMomoTestOrder, {});
      setTestResult({ ok: true, ...data });
    } catch (err) {
      const d = err?.response?.data;
      setTestResult({
        ok: false,
        message: d?.message || err.message || "Gateway test failed.",
        status_code: d?.status_code,
        status_message: d?.status_message,
      });
    } finally { setTesting(false); }
  };

  const enabled = !!form.enabled;
  const isMock  = !!form.mock;

  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<ZenithMomoIcon fontSize="small" />} color="#e5322a" />}
        title="Payment Gateway"
        subheader="Zenith GlobalPay — mobile money credentials used by the cashier's Payment dialog"
        action={
          <Stack direction="row" spacing={0.75} sx={{ pt: 1, pr: 1 }}>
            <Chip
              size="small"
              variant="outlined"
              label={enabled ? "Enabled" : "Disabled"}
              sx={{
                height: 22, fontWeight: 700, fontSize: "0.66rem",
                color: enabled ? "#16a34a" : "text.disabled",
                borderColor: enabled ? alpha("#16a34a", 0.5) : "divider",
                bgcolor: enabled ? alpha("#16a34a", 0.08) : "transparent",
              }}
            />
            {isMock && (
              <Chip
                size="small"
                variant="outlined"
                label="Test mode"
                sx={{
                  height: 22, fontWeight: 700, fontSize: "0.66rem",
                  color: "#b45309", borderColor: alpha("#f59e0b", 0.5), bgcolor: alpha("#f59e0b", 0.10),
                }}
              />
            )}
          </Stack>
        }
      />
      <Divider />
      <CardContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
              <FormControlLabel
                control={<Switch checked={enabled} onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))} />}
                label={<Typography sx={{ fontSize: "0.86rem", fontWeight: 600 }}>Enable Zenith gateway</Typography>}
              />
              <FormControlLabel
                control={<Switch checked={isMock} onChange={(e) => setForm((p) => ({ ...p, mock: e.target.checked }))} />}
                label={<Typography sx={{ fontSize: "0.86rem", fontWeight: 600 }}>Test / mock mode</Typography>}
              />
            </Stack>

            {enabled && isMock && (
              <Alert severity="warning" icon={<RocketIcon fontSize="small" />} sx={{ mb: 2, borderRadius: 2, fontSize: "0.82rem" }}>
                <AlertTitle sx={{ fontSize: "0.85rem", fontWeight: 700 }}>Test mode is on</AlertTitle>
                Every payment is simulated locally — no Zenith call, no prompt on the customer's
                phone, no money moved. The full cashier flow still runs so it can be checked end
                to end. Turn this off once the live API key is in.
              </Alert>
            )}
            {!enabled && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2, fontSize: "0.82rem" }}>
                Gateway disabled. The "Mobile Money (Zenith)" option will error out if a cashier
                picks it — cash and manual mobile money are unaffected.
              </Alert>
            )}

            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, mb: 0.5 }}>API key</Typography>
                <TextField
                  fullWidth size="small"
                  value={keyDirty ? form.api_key : (form.api_key_masked || "")}
                  onChange={set("api_key")}
                  onFocus={() => { if (!keyDirty) setForm((f) => ({ ...f, api_key: "" })); }}
                  placeholder={form.api_key_masked ? "Leave blank to keep the current key" : "Paste the API key Zenith issued"}
                  type={showKey ? "text" : "password"}
                  disabled={!enabled}
                  helperText={form.api_key_masked ? "Leave blank to keep the stored key." : "No key stored yet."}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowKey((s) => !s)} disabled={!enabled}>
                          {showKey ? <HideKeyIcon fontSize="small" /> : <ShowKeyIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, mb: 0.5 }}>GPID</Typography>
                <TextField
                  fullWidth size="small"
                  value={form.gpid}
                  onChange={set("gpid")}
                  placeholder="e.g. GPTEST0109"
                  disabled={!enabled}
                  helperText="Merchant id assigned by Zenith."
                />
              </Grid>

              <Grid item xs={12} sm={12} md={4}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, mb: 0.5 }}>Merchant name</Typography>
                <TextField
                  fullWidth size="small"
                  value={form.merchant_name}
                  onChange={set("merchant_name")}
                  placeholder="Shown to the customer"
                  inputProps={{ maxLength: 30 }}
                  disabled={!enabled}
                  helperText={`${String(form.merchant_name || "").length}/30 — appears on the payment record.`}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, mb: 0.5 }}>API base URL</Typography>
                <TextField
                  fullWidth size="small"
                  value={form.base_url}
                  onChange={set("base_url")}
                  disabled={!enabled}
                  helperText="Zenith's GlobalPay v2 endpoint. Rarely needs changing."
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2.5 }} />
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, mb: 1 }}>
              Patient SMS
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={<Switch size="small" checked={!!form.sms_on_prompt} onChange={(e) => setForm((p) => ({ ...p, sms_on_prompt: e.target.checked }))} />}
                label={
                  <Typography sx={{ fontSize: "0.82rem" }}>
                    Text the patient when the payment prompt is sent
                    <Typography component="span" sx={{ fontSize: "0.74rem", color: "text.secondary", display: "block" }}>
                      Explains what the prompt on their phone is and quotes the reference — cuts down on declined prompts from customers who assume it's a scam.
                    </Typography>
                  </Typography>
                }
              />
              <FormControlLabel
                control={<Switch size="small" checked={!!form.sms_on_receipt} onChange={(e) => setForm((p) => ({ ...p, sms_on_receipt: e.target.checked }))} />}
                label={
                  <Typography sx={{ fontSize: "0.82rem" }}>
                    Text the patient a receipt when payment is confirmed
                    <Typography component="span" sx={{ fontSize: "0.74rem", color: "text.secondary", display: "block" }}>
                      Sent once, on the poll that first sees PAID. Uses the SMS credentials already configured for bulk SMS.
                    </Typography>
                  </Typography>
                }
              />
            </FormGroup>

            {/* Action row */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{ mt: 3, pt: 2, borderTop: "1px dashed", borderColor: "divider" }}
            >
              <Button
                onClick={handleSave}
                disabled={!dirty || saving}
                variant="contained"
                disableElevation
                startIcon={saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SaveIcon fontSize="small" />}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, minWidth: 150 }}
              >
                {saving ? "Saving…" : dirty ? "Save settings" : "No changes"}
              </Button>
              <Button
                onClick={load}
                disabled={loading}
                variant="outlined"
                startIcon={<RefreshIcon fontSize="small" />}
                sx={{ borderRadius: 2, textTransform: "none" }}
              >
                Reload
              </Button>
              <Box sx={{ flex: 1 }} />
              <Tooltip title={dirty ? "Save your changes first." : "Creates a 0.10 GHS order to check the credentials. No debit prompt is sent."}>
                <span>
                  <Button
                    onClick={runTest}
                    disabled={testing || !enabled || dirty}
                    variant="outlined"
                    startIcon={testing ? <CircularProgress size={16} /> : <ApproveIcon fontSize="small" />}
                    sx={{ borderRadius: 2, textTransform: "none" }}
                  >
                    {testing ? "Testing…" : "Test credentials"}
                  </Button>
                </span>
              </Tooltip>
            </Stack>

            {testResult && (
              <Alert severity={testResult.ok ? "success" : "error"} sx={{ mt: 2, borderRadius: 2 }}>
                <AlertTitle sx={{ fontSize: "0.85rem", fontWeight: 700 }}>
                  {testResult.ok
                    ? (testResult.mock ? "Mock test succeeded" : "Zenith responded successfully")
                    : "Zenith rejected the test"}
                </AlertTitle>
                <Typography sx={{ fontSize: "0.8rem" }}>
                  {testResult.message || testResult.status_message}
                  {testResult.status_code ? ` (statusCode ${testResult.status_code})` : ""}
                </Typography>
                {testResult.order_id && (
                  <Typography sx={{ fontSize: "0.72rem", fontFamily: "monospace", mt: 0.5 }}>
                    Order ID: {testResult.order_id}
                  </Typography>
                )}
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", mb: 1 }}>
              Notes
            </Typography>
            <Stack spacing={0.75}>
              <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                <PhoneIphoneIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
                The cashier flow calls <code>SecurePaymentRequest</code> → <code>AuthenticateMOMO</code>, then polls{" "}
                <code>ConfirmTransaction</code>. A bill is only marked paid when Zenith reports <code>PAID</code> —
                a slow answer is treated as "still waiting", never as a decline.
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                <LockIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
                The stored API key is never sent back to the browser — only the masked marker{" "}
                <code>{form.api_key_masked || "••••"}</code>.
              </Typography>
              <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
                <TuneIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
                The cashier only sees the "Mobile Money (Zenith)" option when{" "}
                <b>Accept Mobile Money via Zenith GlobalPay</b> is also on under Module Toggles.
              </Typography>
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Module Toggles ──────────────────────────────────────────────────────
// Facility-level feature switches stored in the `facility_settings` table
// (migration 023). Each row in MODULE_TOGGLES becomes one labelled Switch.
// Adding a new toggle is a single MODULE_TOGGLES entry — no new endpoints,
// no menu rewiring — and the consuming page just has to read the key off
// /facilitySettings. Today it carries one toggle: the request action on
// the Medical Records desk.
const MODULE_TOGGLES = [
  {
    key: "records_request_enabled",
    label: "Allow requests from Medical Records",
    description:
      "When ON, the Medical Records desk can request labs, scans and other services straight from a patient row. " +
      "Turn OFF for facilities where every clinical request must come from a doctor — the request icon disappears across Medical Records.",
    icon: <RequestIcon fontSize="small" />,
    accent: "#7c3aed",
    defaultValue: true,
  },
  {
    key: "login_style_split",
    label: "Use split-screen login layout",
    description:
      "When ON, the /login page shows a two-pane Welcome Back layout — sign-in form on the left and a rotating image carousel with diagnostic-services copy on the right. " +
      "This is the standard login. Turn OFF only to fall back to the older classic single-pane login. Either layout uses the same username + password and the same login backend; only the look changes.",
    icon: <LoginStyleIcon fontSize="small" />,
    accent: "#2563eb",
    defaultValue: true,
  },
  {
    key: "login_use_original_photos",
    label: "Use the original facility photos on the login page and website",
    description:
      "When ON, the split-screen login and the public website show the original building photos that ship with the system (the /pic folder) instead of the neutral default images. " +
      "Leave OFF for any facility those photos weren't taken at. On the login page, pictures uploaded under Appearance → Login Page Images always take priority over both.",
    icon: <LoginImagesIcon fontSize="small" />,
    accent: "#0f766e",
    // Matches LoginSplit: ON by default only on the Dekyi build, so saving
    // this panel there never switches their photos off by accident.
    defaultValue: IS_ORIGINAL_PHOTO_SITE,
  },
  {
    key: "lab_price_info_enabled",
    label: "Show pricing info icon on lab + scan setup",
    description:
      "When ON, an info icon appears next to every lab on the Laboratory dashboard and next to every scan on the Setup Scan page. Lab and radiology staff click it to see the tariff for every insurance partner (plus G-DRG and uninsured rates), and can file a price-change request that lands in the admin queue below. " +
      "Turn OFF and the icon disappears entirely — staff won't see pricing or the request button at all.",
    icon: <LabInfoIcon fontSize="small" />,
    accent: "#0ea5e9",
    defaultValue: true,
  },
  {
    key: "zenith_momo_enabled",
    label: "Accept Mobile Money via Zenith GlobalPay",
    description:
      "When ON, a \"Mobile Money (Zenith)\" option appears in the cashier's Payment dialog. The customer gets a debit prompt on their phone plus an SMS explaining it, and funds settle into the facility's Zenith merchant account. Credentials are set under Billing → Payment Gateway on this page — not in .env. " +
      "Turn OFF for facilities without a Zenith merchant account — the option disappears from the payment dialog and existing Mobile Money (manual) / Cash / Credit modes are unaffected.",
    icon: <ZenithMomoIcon fontSize="small" />,
    accent: "#e5322a",
    defaultValue: false,
  },
];

const toBool = (v) => {
  if (v === true || v === 1) return true;
  if (v === false || v === 0 || v === null || v === undefined) return false;
  const s = String(v).trim().toLowerCase();
  return s === "true" || s === "1" || s === "yes" || s === "on";
};

function ModuleTogglesSettings() {
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [values,  setValues]  = useState({});
  const [meta,    setMeta]    = useState({});
  const [dirty,   setDirty]   = useState(false);

  const updatedByLabel = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("userInfo") || "{}");
      return u?.name || u?.username || "admin";
    } catch (_) { return "admin"; }
  })();

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(facilitySettingsAPI);
      const byKey = new Map();
      if (data?.success && Array.isArray(data.results)) {
        data.results.forEach((r) => byKey.set(r.key, r));
      }
      const nv = {}, nm = {};
      MODULE_TOGGLES.forEach((f) => {
        const row = byKey.get(f.key);
        nv[f.key] = row ? toBool(row.value) : !!f.defaultValue;
        nm[f.key] = row ? { updated_at: row.updated_at, updated_by: row.updated_by } : {};
      });
      setValues(nv); setMeta(nm); setDirty(false);
    } catch (_) {
      // Fall back to defaults if the endpoint or table isn't available.
      const nv = {};
      MODULE_TOGGLES.forEach((f) => { nv[f.key] = !!f.defaultValue; });
      setValues(nv); setMeta({}); setDirty(false);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchSettings(); }, []);

  const handleToggle = (key) => {
    setValues((prev) => ({ ...prev, [key]: !prev[key] }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        settings: Object.fromEntries(
          Object.entries(values).map(([k, v]) => [k, v ? "true" : "false"])
        ),
        updatedBy: updatedByLabel,
      };
      const { data } = await axios.post(facilitySettingsAPI, payload);
      if (!data?.success) throw new Error(data?.message || "Save failed");
      await Swal.fire({
        icon: "success",
        title: "Module toggles saved",
        text: "Changes apply instantly across every workstation.",
        timer: 2200,
        showConfirmButton: false,
      });
      await fetchSettings();
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Save failed",
        text: err?.response?.data?.message || err.message || "Unable to save.",
      });
    } finally { setSaving(false); }
  };

  const formatStamp = (ts, who) => {
    if (!ts) return "Not yet changed";
    try {
      const str = new Date(ts).toLocaleString();
      return who ? `Last changed by ${who} · ${str}` : str;
    } catch (_) { return "Last changed"; }
  };

  // The Save Changes button lives in CardContent's footer rather than in
  // CardHeader because the enclosing SettingsAccordion hides every inner
  // CardHeader (so the accordion's own summary acts as the only header).
  // A hidden Save button = no way to persist toggles — keep it inline.
  const SaveBar = (
    <Box sx={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      mt: 2, pt: 2, borderTop: "1px dashed", borderColor: "divider",
      gap: 1, flexWrap: "wrap",
    }}>
      <Typography sx={{ fontSize: "0.76rem", color: "text.secondary" }}>
        {dirty
          ? "You have unsaved changes."
          : "All changes saved. Toggle a switch above to make changes."}
      </Typography>
      <Button
        size="small"
        variant="contained"
        disableElevation
        startIcon={saving ? <CircularProgress size={14} sx={{ color: "white" }} /> : <SaveIcon fontSize="small" />}
        onClick={handleSave}
        disabled={!dirty || saving || loading}
        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
      >
        {saving ? "Saving…" : dirty ? "Save Changes" : "No Changes"}
      </Button>
    </Box>
  );

  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<TuneIcon fontSize="small" />} color="#7c3aed" />}
        title="Module Toggles"
        subheader="Switch optional system modules on or off for this facility"
      />
      <Divider />
      <CardContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={1.25}>
            {MODULE_TOGGLES.map((f) => {
              const on = !!values[f.key];
              return (
                <Box
                  key={f.key}
                  sx={{
                    display: "flex", alignItems: "flex-start", gap: 1.5,
                    p: 1.5, borderRadius: 2,
                    border: "1.5px solid",
                    borderColor: on ? alpha(f.accent, 0.45) : "divider",
                    bgcolor: on ? alpha(f.accent, 0.05) : "transparent",
                    transition: "all 0.18s",
                  }}
                >
                  <Box sx={{
                    width: 36, height: 36, borderRadius: 1.5, flexShrink: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    bgcolor: on ? alpha(f.accent, 0.18) : alpha("#94a3b8", 0.12),
                    color: on ? f.accent : "text.disabled",
                    border: `1px solid ${on ? alpha(f.accent, 0.35) : "transparent"}`,
                  }}>
                    {f.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: "text.primary" }}>
                        {f.label}
                      </Typography>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={on ? "Enabled" : "Disabled"}
                        sx={{
                          height: 20, fontWeight: 700, fontSize: "0.66rem",
                          color: on ? f.accent : "text.disabled",
                          borderColor: on ? alpha(f.accent, 0.5) : "divider",
                          bgcolor: on ? alpha(f.accent, 0.10) : "transparent",
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", mt: 0.5, lineHeight: 1.5 }}>
                      {f.description}
                    </Typography>
                    <Typography sx={{ fontSize: "0.68rem", color: "text.disabled", mt: 0.5 }}>
                      {formatStamp(meta[f.key]?.updated_at, meta[f.key]?.updated_by)}
                    </Typography>
                  </Box>
                  <Switch
                    checked={on}
                    onChange={() => handleToggle(f.key)}
                    disabled={saving}
                    sx={{
                      "& .MuiSwitch-track": {
                        bgcolor: on ? `${f.accent} !important` : undefined,
                        opacity: on ? "1 !important" : undefined,
                      },
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
        )}
        {!loading && SaveBar}
      </CardContent>
    </Card>
  );
}

// ── Lab / Scan Price Change Requests ────────────────────────────────────
// Admin review queue for tariff corrections filed by lab + radiology
// staff via the info icon on the parameter pages. Approving applies the
// new value to the pricing table (`lab_prices` or `scan_price`) and
// stamps the audit columns; rejecting just marks the request as such.
// Pending requests are shown by default — flip to "All" to see history.
function PriceChangeRequestsPanel() {
  const [tab,      setTab]      = useState("lab");        // 'lab' | 'scan'
  const [filter,   setFilter]   = useState("pending");    // 'pending' | 'all'
  const [rows,     setRows]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [acting,   setActing]   = useState(null);         // {id, action} during a call

  const reviewedBy = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("userInfo") || "{}");
      return u?.name || u?.username || "admin";
    } catch (_) { return "admin"; }
  })();

  const fetchList = async () => {
    setLoading(true);
    try {
      const url = tab === "lab" ? labPriceChangeRequestsList : scanPriceChangeRequestsList;
      const params = filter === "pending" ? { status: "pending" } : {};
      const { data } = await axios.get(url, { params });
      setRows(data?.success ? (data.results || []) : []);
    } catch (_) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [tab, filter]);

  const handleApprove = async (row) => {
    const which = tab === "lab" ? "lab tariff" : "scan tariff";
    const target = tab === "lab" ? row.lab : row.scan;
    const newAmt = tab === "lab" ? row.requested_cost : row.requested_tariff;
    const r = await Swal.fire({
      title: `Approve ${which} change?`,
      html: `<div style="text-align:left;font-size:14px;line-height:1.5">
        <p><strong>${target}</strong> · ${row.insurance || "Uninsured / general"}</p>
        <p>New value: <strong>${Number(newAmt).toFixed(2)}</strong></p>
        <p style="color:#6b7280;font-size:12px">This writes the value to the pricing table immediately.</p>
      </div>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
      confirmButtonColor: "#16a34a",
      cancelButtonText: "Cancel",
    });
    if (!r.isConfirmed) return;
    setActing({ id: row.id, action: "approve" });
    try {
      const { data } = await axios.post(priceChangeRequestApprove(tab, row.id), { reviewedBy });
      if (data?.success) {
        await Swal.fire({ icon: "success", title: "Approved", timer: 1500, showConfirmButton: false });
        fetchList();
      } else {
        await Swal.fire({ icon: "error", title: "Approve failed", text: data?.message || "Try again." });
      }
    } catch (err) {
      await Swal.fire({ icon: "error", title: "Approve failed", text: err.response?.data?.message || err.message });
    } finally {
      setActing(null);
    }
  };

  const handleReject = async (row) => {
    const r = await Swal.fire({
      title: "Reject this request?",
      text: "The request will be marked rejected. Nothing changes in the pricing table.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Reject",
      confirmButtonColor: "#dc2626",
      cancelButtonText: "Cancel",
    });
    if (!r.isConfirmed) return;
    setActing({ id: row.id, action: "reject" });
    try {
      const { data } = await axios.post(priceChangeRequestReject(tab, row.id), { reviewedBy });
      if (data?.success) {
        await Swal.fire({ icon: "success", title: "Rejected", timer: 1200, showConfirmButton: false });
        fetchList();
      } else {
        await Swal.fire({ icon: "error", title: "Reject failed", text: data?.message || "Try again." });
      }
    } catch (err) {
      await Swal.fire({ icon: "error", title: "Reject failed", text: err.response?.data?.message || err.message });
    } finally {
      setActing(null);
    }
  };

  const stamp = (ts) => {
    if (!ts) return "—";
    try { return new Date(ts).toLocaleString(); } catch (_) { return ts; }
  };

  const pendingCount = rows.filter((r) => r.status === "pending").length;

  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<PriceChangeIcon fontSize="small" />} color="#f59e0b" />}
        title="Lab / Scan Price Change Requests"
        subheader="Tariff corrections filed by lab + radiology staff"
      />
      <Divider />
      <CardContent>
        <Box sx={{
          display: "flex", flexWrap: "wrap", gap: 1, mb: 2,
          alignItems: "center", justifyContent: "space-between",
        }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              size="small"
              variant={tab === "lab" ? "contained" : "outlined"}
              onClick={() => setTab("lab")}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
            >
              Lab
            </Button>
            <Button
              size="small"
              variant={tab === "scan" ? "contained" : "outlined"}
              onClick={() => setTab("scan")}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
            >
              Scan
            </Button>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <Chip
              size="small"
              label={`${pendingCount} pending`}
              sx={{ fontWeight: 700, bgcolor: pendingCount ? "rgba(245,158,11,0.18)" : "rgba(148,163,184,0.18)", color: pendingCount ? "#f59e0b" : "#64748b" }}
            />
            <Button
              size="small"
              variant={filter === "pending" ? "contained" : "outlined"}
              onClick={() => setFilter("pending")}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
            >
              Pending
            </Button>
            <Button
              size="small"
              variant={filter === "all" ? "contained" : "outlined"}
              onClick={() => setFilter("all")}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
            >
              All
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={26} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
            <Typography sx={{ fontWeight: 700 }}>
              {filter === "pending" ? "No pending requests." : "No requests on file."}
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", mt: 0.5 }}>
              Lab + radiology staff can file requests from the info icon on the parameter pages.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>{tab === "lab" ? "Lab" : "Scan"}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Insurance</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Current</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Requested</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>By</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const isLab     = tab === "lab";
                  const target    = isLab ? r.lab               : r.scan;
                  const curVal    = isLab ? r.current_cost      : r.current_tariff;
                  const reqVal    = isLab ? r.requested_cost    : r.requested_tariff;
                  const isPending = r.status === "pending";
                  const isBusy    = acting?.id === r.id;
                  return (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{target}</TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>{r.insurance || "—"}</TableCell>
                      <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                        {curVal != null && Number.isFinite(Number(curVal)) ? Number(curVal).toFixed(2) : "—"}
                      </TableCell>
                      <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: 700 }}>
                        {Number(reqVal).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: "0.78rem", maxWidth: 240 }}>
                        <Tooltip title={r.reason || ""}><span style={{ display: "inline-block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 220 }}>{r.reason || "—"}</span></Tooltip>
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: "0.78rem" }}>{r.requested_by || "—"}</TableCell>
                      <TableCell sx={{ color: "text.disabled", fontSize: "0.76rem" }}>{stamp(r.created_at)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={r.status}
                          sx={{
                            height: 20, fontWeight: 700, textTransform: "capitalize", fontSize: "0.7rem",
                            bgcolor: r.status === "approved" ? "rgba(34,197,94,0.18)"
                              : r.status === "rejected" ? "rgba(239,68,68,0.18)"
                              : "rgba(245,158,11,0.18)",
                            color: r.status === "approved" ? "#16a34a"
                              : r.status === "rejected" ? "#dc2626"
                              : "#f59e0b",
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        {isPending ? (
                          <>
                            <Tooltip title="Approve — applies the new value to the pricing table">
                              <span>
                                <IconButton size="small" onClick={() => handleApprove(r)} disabled={isBusy} sx={{ color: "#16a34a" }}>
                                  {isBusy && acting?.action === "approve" ? <CircularProgress size={14} /> : <ApproveIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Reject — marks the request rejected">
                              <span>
                                <IconButton size="small" onClick={() => handleReject(r)} disabled={isBusy} sx={{ color: "#dc2626", ml: 0.25 }}>
                                  {isBusy && acting?.action === "reject" ? <CircularProgress size={14} /> : <RejectIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        ) : (
                          <Typography sx={{ fontSize: "0.72rem", color: "text.disabled" }}>
                            {r.reviewed_by ? `by ${r.reviewed_by}` : "—"}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

// ── Login Page Images ───────────────────────────────────────────────────
// Facility-owned pictures for the split-screen login carousel (/loginSlides,
// migration 046). With no pictures uploaded the login shows the neutral
// bundled defaults, so a new facility never displays another site's photos.
// Uploads are resized + re-encoded as JPEG in the browser to keep each one
// a few hundred KB.
const LOGIN_IMG_MAX_W = 1920;

function compressLoginImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the file."));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("That file is not a readable picture."));
      img.onload = () => {
        const scale  = Math.min(1, LOGIN_IMG_MAX_W / img.width);
        const canvas = document.createElement("canvas");
        canvas.width  = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function LoginImagesSettings() {
  const [loading, setLoading] = useState(true);
  const [busy,    setBusy]    = useState(false);
  const [slides,  setSlides]  = useState([]);
  const [dirtyIds, setDirtyIds] = useState(new Set());

  const updatedByLabel = (() => {
    try {
      const u = JSON.parse(localStorage.getItem("userInfo") || "{}");
      return u?.name || u?.username || "admin";
    } catch (_) { return "admin"; }
  })();

  const fetchSlides = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(loginSlidesAPI, { params: { all: 1 } });
      setSlides(data?.success && Array.isArray(data.results) ? data.results : []);
      setDirtyIds(new Set());
    } catch (_) {
      setSlides([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchSlides(); }, []);

  const fail = (err) => Swal.fire({
    icon: "error",
    title: "Something went wrong",
    text: err?.response?.data?.message || err.message || "Unable to save.",
  });

  const saveSlide = (s, extra = {}) => axios.post(loginSlidesAPI, {
    id: s.id, title: s.title, body: s.body,
    sort_order: s.sort_order, is_active: !!Number(s.is_active),
    updatedBy: updatedByLabel, ...extra,
  });

  const handleAdd = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      let order = slides.reduce((m, s) => Math.max(m, Number(s.sort_order) || 0), 0);
      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const image = await compressLoginImage(file);
        order += 1;
        // eslint-disable-next-line no-await-in-loop
        const { data } = await axios.post(loginSlidesAPI, {
          image, title: "", body: "", sort_order: order, is_active: true, updatedBy: updatedByLabel,
        });
        if (!data?.success) throw new Error(data?.message || "Upload failed");
      }
      await fetchSlides();
    } catch (err) { await fail(err); await fetchSlides(); }
    finally { setBusy(false); }
  };

  const handleReplace = async (s, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const image = await compressLoginImage(file);
      const { data } = await saveSlide(s, { image });
      if (!data?.success) throw new Error(data?.message || "Upload failed");
      await fetchSlides();
    } catch (err) { await fail(err); }
    finally { setBusy(false); }
  };

  const editField = (id, field, value) => {
    setSlides((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
    setDirtyIds((prev) => new Set(prev).add(id));
  };

  const handleSaveText = async (s) => {
    setBusy(true);
    try {
      const { data } = await saveSlide(s);
      if (!data?.success) throw new Error(data?.message || "Save failed");
      setDirtyIds((prev) => { const n = new Set(prev); n.delete(s.id); return n; });
    } catch (err) { await fail(err); }
    finally { setBusy(false); }
  };

  const handleToggleActive = async (s) => {
    setBusy(true);
    try {
      await saveSlide({ ...s, is_active: Number(s.is_active) ? 0 : 1 });
      await fetchSlides();
    } catch (err) { await fail(err); }
    finally { setBusy(false); }
  };

  // Swap positions with the neighbour, then persist both orders.
  const handleMove = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= slides.length) return;
    const list = [...slides];
    [list[idx], list[j]] = [list[j], list[idx]];
    setBusy(true);
    try {
      await Promise.all(list.map((s, i) => saveSlide({ ...s, sort_order: i + 1 })));
      await fetchSlides();
    } catch (err) { await fail(err); }
    finally { setBusy(false); }
  };

  const handleDelete = async (s) => {
    const ok = await Swal.fire({
      icon: "warning",
      title: "Remove this picture?",
      text: "It will no longer show on the login page.",
      showCancelButton: true,
      confirmButtonText: "Remove",
      confirmButtonColor: "#dc2626",
    });
    if (!ok.isConfirmed) return;
    setBusy(true);
    try {
      await axios.delete(`${loginSlidesAPI}/${s.id}`);
      await fetchSlides();
    } catch (err) { await fail(err); }
    finally { setBusy(false); }
  };

  const usingDefaults = !slides.some((s) => Number(s.is_active));

  return (
    <Card>
      <CardHeader
        avatar={<IconBox icon={<LoginImagesIcon fontSize="small" />} color="#2563eb" />}
        title="Login Page Images"
        subheader="Pictures on the split-screen login page"
      />
      <Divider />
      <CardContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Alert severity={usingDefaults ? "info" : "success"} sx={{ mb: 2 }}>
              {usingDefaults
                ? "No pictures uploaded — the login page shows the default images below (or the original facility photos, if that switch is on under Module Toggles). Upload this facility's own photos to replace them."
                : "The login page is showing this facility's own pictures. Remove or hide them all to go back to the default images."}
            </Alert>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
              <Button
                component="label"
                variant="contained"
                disableElevation
                disabled={busy}
                startIcon={busy ? <CircularProgress size={14} sx={{ color: "white" }} /> : <AddPhotoIcon fontSize="small" />}
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
              >
                {busy ? "Working…" : "Upload pictures"}
                <input hidden type="file" accept="image/png,image/jpeg,image/webp" multiple onChange={handleAdd} />
              </Button>
              <Typography sx={{ fontSize: "0.76rem", color: "text.secondary" }}>
                Landscape photos work best (at least 1600 × 1000). Title and caption are optional — blank ones use the default wording.
              </Typography>
            </Box>

            {slides.length === 0 ? (
              <Grid container spacing={1.5}>
                {LOGIN_DEFAULT_SLIDES.map((d) => (
                  <Grid item xs={6} sm={4} md={3} key={d.img}>
                    <Box sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
                      <Box component="img" src={d.img} alt={d.title} sx={{ width: "100%", aspectRatio: "16 / 10", objectFit: "cover", display: "block" }} />
                      <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, p: 0.75 }} noWrap>{d.title}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Stack spacing={1.25}>
                {slides.map((s, idx) => {
                  const active = !!Number(s.is_active);
                  const fallback = LOGIN_DEFAULT_SLIDES[idx % LOGIN_DEFAULT_SLIDES.length];
                  return (
                    <Box key={s.id} sx={{
                      display: "flex", gap: 1.5, p: 1.25, borderRadius: 2, flexWrap: { xs: "wrap", sm: "nowrap" },
                      border: "1.5px solid", borderColor: active ? alpha("#2563eb", 0.4) : "divider",
                      opacity: active ? 1 : 0.6,
                    }}>
                      <Tooltip title="Click to replace this picture">
                        <Box component="label" sx={{ cursor: busy ? "default" : "pointer", flexShrink: 0 }}>
                          <Box component="img" src={s.image} alt="" sx={{
                            width: 180, aspectRatio: "16 / 10", objectFit: "cover", borderRadius: 1.5, display: "block",
                          }} />
                          <input hidden type="file" accept="image/png,image/jpeg,image/webp" disabled={busy}
                                 onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleReplace(s, f); }} />
                        </Box>
                      </Tooltip>
                      <Stack spacing={1} sx={{ flex: 1, minWidth: 220 }}>
                        <TextField size="small" label="Title (optional)" placeholder={fallback.title}
                                   value={s.title || ""} inputProps={{ maxLength: 160 }}
                                   onChange={(e) => editField(s.id, "title", e.target.value)} />
                        <TextField size="small" label="Caption (optional)" placeholder={fallback.body}
                                   value={s.body || ""} multiline minRows={2} inputProps={{ maxLength: 500 }}
                                   onChange={(e) => editField(s.id, "body", e.target.value)} />
                        {dirtyIds.has(s.id) && (
                          <Box>
                            <Button size="small" variant="outlined" startIcon={<SaveIcon fontSize="small" />}
                                    disabled={busy} onClick={() => handleSaveText(s)}
                                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}>
                              Save text
                            </Button>
                          </Box>
                        )}
                      </Stack>
                      <Stack alignItems="center" spacing={0.25}>
                        <Tooltip title={active ? "Showing on login — click to hide" : "Hidden — click to show"}>
                          <Switch checked={active} disabled={busy} onChange={() => handleToggleActive(s)} />
                        </Tooltip>
                        <Tooltip title="Move up">
                          <span><IconButton size="small" disabled={busy || idx === 0} onClick={() => handleMove(idx, -1)}><MoveUpIcon fontSize="small" /></IconButton></span>
                        </Tooltip>
                        <Tooltip title="Move down">
                          <span><IconButton size="small" disabled={busy || idx === slides.length - 1} onClick={() => handleMove(idx, 1)}><MoveDownIcon fontSize="small" /></IconButton></span>
                        </Tooltip>
                        <Tooltip title="Remove">
                          <span><IconButton size="small" color="error" disabled={busy} onClick={() => handleDelete(s)}><DeleteIcon fontSize="small" /></IconButton></span>
                        </Tooltip>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function HospitalAdminSettings() {
  // Section metadata — drives the collapsible list. To add a new setting
  // panel, add a row here. Sections are grouped visually with a short
  // subheader so the list never becomes a wall of pills.
  const SECTIONS = [
    {
      group: "Billing & Authorization",
      items: [
        { id: "credit",       icon: <CreditCardOutlined fontSize="small" />, color: "#1a6b8a", title: "Credit & Billing Permissions",   subtitle: "Control which patient bills are eligible for crediting without approval", component: <CreditBillingSettings /> },
        { id: "auth",         icon: <LockOpenOutlined fontSize="small" />,   color: "#e07b39", title: "Credit Authorization Requests",  subtitle: "Pending billing-side requests requiring administrative approval",         component: <CreditAuthorizationRequests /> },
        { id: "idcard",       icon: <IdCardIcon fontSize="small" />,         color: "#0d7a71", title: "ID Card Amount",                 subtitle: "Set the registration/ID card fee charged to new patients",                component: <IdCardAmountSettings /> },
        { id: "gateway",      icon: <ZenithMomoIcon fontSize="small" />,     color: "#e5322a", title: "Payment Gateway",                subtitle: "Zenith GlobalPay mobile-money credentials, test mode, and patient SMS notifications", component: <PaymentGatewaySettings /> },
      ],
    },
    {
      group: "Module Settings",
      items: [
        { id: "moduletoggles", icon: <TuneIcon fontSize="small" />,          color: "#7c3aed", title: "Module Toggles",                 subtitle: "Switch optional system modules on or off for this facility (e.g. requests from Medical Records)", component: <ModuleTogglesSettings /> },
        { id: "pricechanges",  icon: <PriceChangeIcon fontSize="small" />,   color: "#f59e0b", title: "Lab / Scan Price Change Requests", subtitle: "Pending tariff corrections filed by lab + radiology staff — approve to apply, reject to dismiss", component: <PriceChangeRequestsPanel /> },
      ],
    },
    {
      group: "Communications",
      items: [
        { id: "sms",          icon: <SmsOutlined fontSize="small" />,        color: "#7b3f9e", title: "SMS Alert Settings",             subtitle: "Configure when the system sends automated SMS notifications",             component: <SmsAlertSettings /> },
        { id: "notifications",icon: <NotificationsActiveOutlined fontSize="small" />, color: "#dc2626", title: "Notification Settings", subtitle: "In-app and push notification preferences",                                component: <NotificationSettings /> },
      ],
    },
    {
      group: "Hospital Setup",
      items: [
        { id: "insurance",    icon: <InsIcon fontSize="small" />,            color: "#1976d2", title: "Insurance Partners",             subtitle: "Manage the list of accepted insurance providers",                         component: <InsurancePartnerSettings /> },
        { id: "compliance",   icon: <VerifiedUserOutlined fontSize="small" />,color: "#15803d", title: "Compliance & Audit Rules",      subtitle: "Vitals frequency, nurse-note cadence, and other clinical SLAs",            component: <ComplianceSettings /> },
        { id: "autoupdate",   icon: <AutorenewOutlined fontSize="small" />,  color: "#0ea5e9", title: "Auto-Update Settings",           subtitle: "Control system-wide automatic data refresh and sync behaviour",           component: <AutoUpdateSettings /> },
        { id: "general",      icon: <SettingsOutlined fontSize="small" />,   color: "#64748b", title: "General Settings",               subtitle: "Miscellaneous facility-wide preferences",                                  component: <GeneralSettings /> },
      ],
    },
    {
      group: "Appearance",
      items: [
        { id: "theme",        icon: <PaletteOutlined fontSize="small" />,    color: "#5468FF", title: "Default System Theme",           subtitle: "Choose the theme everyone sees by default — users can still override on their own account", component: <SystemThemeSettings /> },
        { id: "loginimages",  icon: <LoginImagesIcon fontSize="small" />,    color: "#2563eb", title: "Login Page Images",              subtitle: "Pictures on the split-screen login — upload this facility's own photos or keep the default images", component: <LoginImagesSettings /> },
      ],
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", p: { xs: 2, md: 3 } }}>
      {/* Page header */}
      <Stack direction="row" alignItems="center" spacing={2} mb={1}>
        <Avatar sx={{
          bgcolor: alpha(SETTINGS_ACCENT, 0.18),
          color: SETTINGS_ACCENT,
          border: `1px solid ${alpha(SETTINGS_ACCENT, 0.30)}`,
          width: 44, height: 44,
        }}>
          <SettingsOutlined />
        </Avatar>
        <Box>
          <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, color: "text.primary", lineHeight: 1.15 }}>
            System Settings
          </Typography>
          <Typography sx={{ fontSize: "0.82rem", color: "text.secondary", mt: 0.25 }}>
            Administrative configuration — click any panel to expand its options.
          </Typography>
        </Box>
      </Stack>

      {/* Collapsible sections grouped by area */}
      <Stack spacing={3} sx={{ mt: 3 }}>
        {SECTIONS.map((g) => (
          <Box key={g.group}>
            <Typography sx={{
              fontSize: "0.7rem", fontWeight: 800, letterSpacing: "1.2px",
              textTransform: "uppercase", color: "text.secondary",
              mb: 1, pl: 0.5,
            }}>
              {g.group}
            </Typography>
            <Stack spacing={1.25}>
              {g.items.map((s) => (
                <SettingsAccordion
                  key={s.id}
                  icon={s.icon}
                  color={s.color}
                  title={s.title}
                  subtitle={s.subtitle}
                >
                  {s.component}
                </SettingsAccordion>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}